import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useGameStore from '../stores/gameStore';
import './Game.css';
import LoserModal from './modals/LoserModal';
import WinnerModal from './modals/WinnerModal';
import Sidebar from './Sidebar';
// import { useMediaQuery } from 'usehooks-ts';
import ChatButton from '../components/ChatButton';
import clsxm from '../utils/clsxm';


const Game = ({ socket, board, draw, username, onHuntAgain, }) => {
  const {
    playerScore,
    setPlayerScore,
    setArsenalItems,
    setSelectedSlot,
    setShowHuntAgain,
    gamePlayers,
    setGamePlayers,
    setOnHuntAgain,
    setOnLeaveGame,
    reconnectionStatus,
  } = useGameStore();
  // const isMobile = useMediaQuery('(min-width: 480px)');
  const [notification, setNotification] = useState('');
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [showLoserModal, setShowLoserModal] = useState(false);
  const [gameOverData, setGameOverData] = useState({
    winners: [],
    currentPlayer: username,
  });
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [scorePopupMessage, setScorePopupMessage] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // const [isPortrait, setIsPortrait] = useState(true);
  // const [isMobile, setIsMobile] = useState(false);
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const navigate = useNavigate();
  const keysPressed = useRef(new Set());
  const movementIntervalRef = useRef(null);
  const lastMoveTimeRef = useRef(0);
  const playerPositions = useRef(new Map()); // Store interpolated positions
  const lastBoardUpdate = useRef(Date.now());

  const handleLeaveGame = useCallback(() => {
    socket.emit('leaveRoom');
    onHuntAgain();
  }, [socket, onHuntAgain]);

  // Removed handleSendMessage - now handled by ChatBox component

  // Mobile touch controls handlers
  const handleMobileMove = useCallback(
    (direction) => {
      if (socket) {
        socket.emit('move', direction);
      }
    },
    [socket],
  );

  const handleMobileBomb = useCallback(() => {
    if (socket) {
      socket.emit('plantBomb');
    }
  }, [socket]);

  // Enhanced mobile touch handlers to prevent context menu
  const handleTouchStart = useCallback(
    (e, action) => {
      e.preventDefault(); // Prevent default touch behaviors
      e.stopPropagation();

      if (typeof action === 'function') {
        action();
      } else if (typeof action === 'string') {
        handleMobileMove(action);
      }
    },
    [handleMobileMove],
  );

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault(); // Prevent context menu
    e.stopPropagation();
    return false;
  }, []);

  // Update game store with current game data
  useEffect(() => {
    setOnHuntAgain(onHuntAgain);
    setOnLeaveGame(handleLeaveGame);
    // setOnSendMessage removed - chat now handled by ChatBox component
  }, [onHuntAgain, handleLeaveGame, setOnHuntAgain, setOnLeaveGame]);


  useEffect(() => {
    if (!socket) return;

    // Handle reconnection success - request current board state
    const handleReconnectionSuccess = () => {
      console.log('🔄 Reconnection detected, requesting current board state');
      socket.emit('getCurrentBoard');
    };

    socket.on('arsenalUpdate', (items) => {
      setArsenalItems(items);
    });

    socket.on('arsenalSelect', (slot) => {
      setSelectedSlot(slot);
    });

    // Removed message handler - now handled by ChatBox component

    socket.on('gameOver', (msg) => {
      setNotification(msg);
      setShowHuntAgain(true);
      localStorage.removeItem('sessionData');
      // Don't clear bomberman session here - let user choose to leave room
    });

    socket.on('playerDied', (player) => {
      if (player.id === socket.id) {
        setNotification('Spectating');
        setShowHuntAgain(true);
        localStorage.removeItem('sessionData');
        // Don't clear bomberman session here - let user choose to leave room

        // Show loser modal for eliminated players
        setGameOverData({
          winners: [],
          currentPlayer: username,
        });
        setShowLoserModal(true);
      }
    });

    socket.on('winnerUpdateBroadcast', (data) => {

      if (data.success && data.winners && data.winnerSocketIds) {
        const isCurrentPlayerWinner = data.winnerSocketIds.includes(socket.id);

        setGameOverData({
          winners: data.winners,
          currentPlayer: username,
        });

        // Show appropriate modal based on whether current player won or lost
        if (isCurrentPlayerWinner) {
          setShowWinnerModal(true);
        } else {
          setShowLoserModal(true);
        }
      }
    });

    socket.on('playerListUpdate', (players) => {
     
      setGamePlayers(players || []);
    });

    // Request current player list when Game component mounts
    socket.emit('getPlayerList');

    // Request current board state after component mounts or reconnection
    // with a small delay to ensure socket is fully connected and game state is ready
    setTimeout(() => {
      console.log('🔄 Requesting current board state on Game component mount');
      socket.emit('getCurrentBoard');
    }, 500);

    socket.on('playerAlert', ({ message }) => {
      // Show score popup for powerup collection
      if (message.includes('+10 points')) {
        setScorePopupMessage(message);
        setShowScorePopup(true);

        // Extract score from message and update player score
        const scoreMatch = message.match(/Total: (\d+)/);
        if (scoreMatch) {
          const newScore = parseInt(scoreMatch[1]);
          setPlayerScore(newScore);
        }

        // Hide popup after 3 seconds
        setTimeout(() => {
          setShowScorePopup(false);
        }, 1000);
      }
    });

    socket.on('gameReset', () => {
      // Reset game UI state
      setNotification('');
      // setShowWinnerModal(false);
      // setShowLoserModal(false);
      setShowScorePopup(false);
      setPlayerScore(0);
      setGameOverData({
        winners: [],
        currentPlayer: username,
      });
    });

    // Handle grace period messages
    socket.on('gracePeriodStarted', () => {
      // console.log('⏰ DEBUG: Grace period started for another player', { message });
      // You can add UI feedback here if needed
    });

    // Listen for reconnection events
    socket.on('reconnect', handleReconnectionSuccess);
    socket.on('sessionRecovered', handleReconnectionSuccess);
    socket.on('rejoinSuccess', handleReconnectionSuccess);

    return () => {
      socket.off('arsenalUpdate');
      socket.off('arsenalSelect');
      // socket.off('message') removed - handled by ChatBox
      socket.off('gameOver');
      socket.off('playerDied');
      socket.off('winnerUpdateBroadcast');
      socket.off('playerListUpdate');
      socket.off('playerAlert');
      socket.off('gameReset');
      socket.off('gracePeriodStarted');
      socket.off('reconnect');
      socket.off('sessionRecovered');
      socket.off('rejoinSuccess');
    };
  }, [
    socket,
    username,
    setArsenalItems,
    setSelectedSlot,
    setShowHuntAgain,
    setPlayerScore,
    setGamePlayers,
    // setMessages removed - handled by ChatBox
  ]);

  useEffect(() => {
    if (!draw || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Setup responsive canvas scaling
    const setupCanvasScaling = () => {
      // Determine if we're on mobile (screen width <= 768px)
      const isMobileScreen = window.innerWidth <= 768;

      // Set canvas dimensions based on screen size
      const DESKTOP_SIZE = 649;
      const INTERNAL_RESOLUTION = 672; // Keep internal resolution consistent for game logic

      let displaySize;
      if (isMobileScreen) {
        // For mobile, use maximum available width minus padding
        const availableWidth = window.innerWidth;
        const padding = 42; // Account for container padding and margins
        const maxMobileSize = Math.min(availableWidth - padding, window.innerHeight - 200); // Leave space for UI
        displaySize = Math.max(250, maxMobileSize); // Minimum size of 250px
      } else {
        displaySize = DESKTOP_SIZE;
      }

      // Set canvas display size
      canvas.style.width = `${displaySize}px`;
      canvas.style.height = `${displaySize}px`;

      // Set canvas internal resolution (consistent for game logic)
      canvas.width = INTERNAL_RESOLUTION;
      canvas.height = INTERNAL_RESOLUTION;

      // Calculate scale factor for proper rendering
      // const scaleFactor = displaySize / INTERNAL_RESOLUTION;

      // Reset and apply scaling to context
      ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
      // We keep the internal scaling at 1:1 for game logic consistency
    };

    // Initial setup
    setupCanvasScaling();

    // Update scaling on window resize
    const handleResize = () => setupCanvasScaling();
    window.addEventListener('resize', handleResize);

    draw.ctx = ctx;

    let lastRenderTime = 0;
    const TARGET_FPS = 60; // 60 FPS for smooth rendering
    const FRAME_INTERVAL = 1000 / TARGET_FPS;
    const INTERPOLATION_SPEED = 0.15; // Smooth interpolation factor (0-1, higher = faster, 0.15 = slower smooth movement)

    // Helper function to smoothly interpolate positions
    const interpolatePosition = (current, target, speed) => {
      if (!current) return target;
      return current + (target - current) * speed;
    };

    const gameLoop = (currentTime) => {
      // Throttle rendering to target FPS
      if (currentTime - lastRenderTime < FRAME_INTERVAL) {
        gameLoopRef.current = requestAnimationFrame(gameLoop);
        return;
      }

      const deltaTime = currentTime - lastRenderTime;
      lastRenderTime = currentTime;

      // Update interpolated positions from board data
      if (board) {
        board.forEach((cell) => {
          if (cell?.tile === 'player' && cell?.id) {
            const playerId = cell.id;
            const targetRow = cell.row;
            const targetColumn = cell.column;

            if (!playerPositions.current.has(playerId)) {
              // First time seeing this player, set position directly
              playerPositions.current.set(playerId, {
                row: targetRow,
                column: targetColumn,
                targetRow,
                targetColumn,
              });
            } else {
              // Update target position
              const pos = playerPositions.current.get(playerId);
              pos.targetRow = targetRow;
              pos.targetColumn = targetColumn;
            }
          }
        });

        // Remove players that are no longer in the board
        const currentPlayerIds = new Set(
          board.filter(cell => cell?.tile === 'player' && cell?.id).map(cell => cell.id)
        );
        for (const [playerId] of playerPositions.current) {
          if (!currentPlayerIds.has(playerId)) {
            playerPositions.current.delete(playerId);
          }
        }
      }

      // Interpolate all player positions towards their targets
      for (const [playerId, pos] of playerPositions.current) {
        pos.row = interpolatePosition(pos.row, pos.targetRow, INTERPOLATION_SPEED);
        pos.column = interpolatePosition(pos.column, pos.targetColumn, INTERPOLATION_SPEED);
      }

      // Render the board
      draw.clear();
      const drawAtEndCallbacks = [];

      if (board) {
        board.forEach((cell) => {
          if (cell?.tile === 'player' && cell?.id) {
            // Use interpolated position for smooth movement
            const interpolatedPos = playerPositions.current.get(cell.id);
            if (interpolatedPos) {
              const callback = draw[cell.tile]?.({
                ...cell,
                row: interpolatedPos.row,
                column: interpolatedPos.column,
                shouldDrawLabel: cell.label !== username,
              });
              if (callback) drawAtEndCallbacks.push(callback);
            }
          } else {
            // Non-player entities render normally
            const callback = draw[cell?.tile]?.({
              ...cell,
              shouldDrawLabel: cell?.label !== username,
            });
            if (callback) drawAtEndCallbacks.push(callback);
          }
        });

        drawAtEndCallbacks.forEach((callback) => callback());
      }

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoop(0);

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [draw, board, username]);


  // Optimized movement system with key state tracking and throttling
  useEffect(() => {
    if (!socket) return;

    const MOVEMENT_RATE = 30; // Send movement 30 times per second for smooth updates
    const MIN_MOVE_INTERVAL = 1000 / MOVEMENT_RATE; // ~33ms

    // Map key codes to movement directions
    const keyToDirection = {
      'ArrowUp': 'moveUp',
      'ArrowDown': 'moveDown',
      'ArrowLeft': 'moveLeft',
      'ArrowRight': 'moveRight',
    };

    const sendMovement = () => {
      const now = Date.now();
      // Throttle movement events to prevent flooding
      if (now - lastMoveTimeRef.current < MIN_MOVE_INTERVAL) {
        return;
      }

      // Send movement for the first pressed key (prioritize first key pressed)
      const keysArray = Array.from(keysPressed.current);
      if (keysArray.length > 0) {
        const direction = keyToDirection[keysArray[0]];
        if (direction) {
          socket.emit('move', direction);
          lastMoveTimeRef.current = now;
        }
      }
    };

    const handleKeyDown = (e) => {
      // Prevent default behavior for game keys
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(e.code)) {
        e.preventDefault();
      }

      if (document.activeElement.id === 'message-field') return;

      // Handle non-movement keys immediately
      if (e.code === 'Space') {
        socket.emit('plantBomb');
        return;
      }
      if (e.code === 'KeyB') {
        socket.emit('changeArsenalItem');
        return;
      }

      // Track movement keys
      if (keyToDirection[e.code]) {
        // Only add if not already pressed (prevent duplicates)
        if (!keysPressed.current.has(e.code)) {
          keysPressed.current.add(e.code);
          // Send immediate movement on key press for instant response
          const direction = keyToDirection[e.code];
          if (direction) {
            socket.emit('move', direction);
            lastMoveTimeRef.current = Date.now();
          }
        }
      }
    };

    const handleKeyUp = (e) => {
      if (keyToDirection[e.code]) {
        keysPressed.current.delete(e.code);
      }
    };

    // Start movement interval for continuous movement while keys are held
    movementIntervalRef.current = setInterval(sendMovement, MIN_MOVE_INTERVAL);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (movementIntervalRef.current) {
        clearInterval(movementIntervalRef.current);
        movementIntervalRef.current = null;
      }
      keysPressed.current.clear();
    };
  }, [socket]);

  const handleGetRewarded = async () => {
    try {
      navigate('/dashboard');
    } catch (error) {
      console.error('Error claiming rewards:', error);
      alert(`Error claiming rewards: ${error.message}`);
    }
  };

  const handlePlayAgain = () => {
    setShowWinnerModal(false);
    setShowLoserModal(false);
    // Reset score for new game
    setPlayerScore(0);
    localStorage.setItem('playerScore', '0');
    onHuntAgain();
    navigate('/game-lobby/lobby');
  };

  const handleBackToLobby = () => {
    setShowWinnerModal(false);
    setShowLoserModal(false);
    // Reset score when going back to lobby
    setPlayerScore(0);
    localStorage.setItem('playerScore', '0');
    onHuntAgain();
  };

  return (
    // full screen height remove 66px from it
    <div
      className={`flex items-center justify-center p-2 md:p-4 pt-[65px] h-full transition-all duration-300 ${sidebarOpen ? 'lg:pr-80 xl:pr-96' : ''}`}
      onContextMenu={(e) => e.preventDefault()} // 🚫 disable right-click/long-press menu
    >
      <div className='flex flex-col gap-2 md:gap-6 max-w-7xl w-full'>
      {/* Reconnection Status Indicators */}
      {reconnectionStatus === 'attempting' && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-yellow-500/90 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center gap-2">
            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
            <span>Reconnecting...</span>
          </div>
        </div>
      )}

      {reconnectionStatus === 'success' && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-green-500/90 text-white px-4 py-2 rounded-lg shadow-lg">
          <span>✅ Reconnected successfully!</span>
        </div>
      )}

      {/* Grace Period Indicator */}
      {reconnectionStatus === 'grace-period' && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-orange-500/90 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center gap-2">
            <div className="animate-pulse w-4 h-4 bg-white rounded-full"></div>
            <span>5 minutes to reconnect...</span>
          </div>
        </div>
      )}
        {/* Game Area */}
        <div className='w-full'>
          <div className='bg-gray-900/80 backdrop-blur-lg rounded-xl md:rounded-2xl border border-gray-700/50 md:p-6 shadow-2xl'>
            {/* Responsive Game Canvas */}
            <div className='flex justify-center'>
              <div className='bg-gray-800/50 rounded-xl md:p-4 border border-gray-600/50 w-full max-w-2xl'>
                <canvas
                  ref={canvasRef}
                  className='rounded-lg shadow-lg block mx-auto'
                  style={{ imageRendering: 'pixelated' }}
                />
              </div>
            </div>

            {/* Score Popup */}
            {showScorePopup && (
              <div className='fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-bounce'>
                <div className='bg-gradient-to-r from-emerald-500 to-cyan-500 text-white px-6 py-3 rounded-lg shadow-lg border border-emerald-400/50'>
                  <div className='flex items-center gap-2'>
                    <i className='fas fa-star text-yellow-300'></i>
                    <span className='font-bold'>{scorePopupMessage}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Game Status Notifications */}
            {notification && (
              <div className='mt-4 p-4 bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-lg border border-purple-500/30 text-center'>
                <div className='text-purple-300 font-bold text-lg'>{notification}</div>
              </div>
            )}
          </div>

          {/* Mobile Touch Controls */}
          <div className='lg:hidden mt-4'>
            {/* Single Line Controls - 2 Left, Bomb Center, 2 Right */}
            <div className='flex items-center justify-between px-4 gap-2'>
              {/* Left Side - 2 Movement Buttons */}
              <div className='flex gap-2'>
              <button
                  onTouchStart={(e) => handleTouchStart(e, 'moveLeft')}
                  onTouchEnd={handleTouchEnd}
                  onContextMenu={handleContextMenu}
                  onClick={() => handleMobileMove('moveLeft')}
                  className='flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg border-2 border-blue-400 active:scale-95 transition-transform duration-100 select-none'
                  style={{
                    touchAction: 'manipulation',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                  }}>
                  <i className='fas fa-arrow-left text-white text-lg pointer-events-none'></i>
                </button>
                <button
                  onTouchStart={(e) => handleTouchStart(e, 'moveRight')}
                  onTouchEnd={handleTouchEnd}
                  onContextMenu={handleContextMenu}
                  onClick={() => handleMobileMove('moveRight')}
                  className='flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg border-2 border-blue-400 active:scale-95 transition-transform duration-100 select-none'
                  style={{
                    touchAction: 'manipulation',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                  }}>
                  <i className='fas fa-arrow-right text-white text-lg pointer-events-none'></i>
                </button>
                
              </div>

              {/* Center - Bomb Button */}
              <button
                onTouchStart={(e) => handleTouchStart(e, handleMobileBomb)}
                onTouchEnd={handleTouchEnd}
                onContextMenu={handleContextMenu}
                onClick={handleMobileBomb}
                className={clsxm('arsenal-slot w-12 h-12 rounded-lg p-1 transition-all duration-200 animate-pulse', 
                          'bg-gray-800/50')}
                style={{
                  touchAction: 'manipulation',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                }}>
                    {/* <BombIcon className='' /> */}
                {/* <i className='fas fa-bomb text-white text-xl pointer-events-none'></i> */}
                <img src={'/sprites/bombs/bomb_normal.png'} alt='bomb' className='w-10 h-10' />
              </button>

              {/* Right Side - 2 Movement Buttons */}
              <div className='flex gap-2'>
              <button
                  onTouchStart={(e) => handleTouchStart(e, 'moveUp')}
                  onTouchEnd={handleTouchEnd}
                  onContextMenu={handleContextMenu}
                  onClick={() => handleMobileMove('moveUp')}
                  className='flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg border-2 border-blue-400 active:scale-95 transition-transform duration-100 select-none'
                  style={{
                    touchAction: 'manipulation',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                  }}>
                  <i className='fas fa-arrow-up text-white text-lg pointer-events-none'></i>
                </button>
                <button
                  onTouchStart={(e) => handleTouchStart(e, 'moveDown')}
                  onTouchEnd={handleTouchEnd}
                  onContextMenu={handleContextMenu}
                  onClick={() => handleMobileMove('moveDown')}
                  className='flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg border-2 border-blue-400 active:scale-95 transition-transform duration-100 select-none'
                  style={{
                    touchAction: 'manipulation',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                  }}>
                  <i className='fas fa-arrow-down text-white text-lg pointer-events-none'></i>
                </button>
              </div>
            </div>

            {/* Arsenal Button - Below the main controls */}
            {/* <div className='flex justify-center mt-4'>
              <button
                onClick={handleMobileArsenal}
                className='flex items-center justify-center w-14 h-14 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg border-2 border-purple-400 active:scale-95 transition-transform duration-100'
              >
                <i className='fas fa-sync-alt text-white text-lg'></i>
              </button>
            </div> */}
          </div>
        </div>

      
        <ChatButton
        onClick={() => setSidebarOpen(true)} 
        isGamePage={sidebarOpen} 
        className='lg:fixed lg:block bottom-4 right-4 z-40'
      />

        {/* Right Sidebar - Players & Chat */}
        <Sidebar
          gamePlayers={gamePlayers}
          playerScore={playerScore}
          socket={socket}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      </div>

      {/* Winner Modal */}
      <WinnerModal
        show={showWinnerModal}
        winners={gameOverData.winners}
        currentPlayer={gameOverData.currentPlayer}
        finalScore={playerScore}
        onGetRewarded={handleGetRewarded}
        onPlayAgain={handlePlayAgain}
        onBackToLobby={handleBackToLobby}
        onClose={() => {
          onHuntAgain();
          setShowWinnerModal(false);
        }}
      />

      {/* Loser Modal */}
      <LoserModal
        show={showLoserModal}
        winners={gameOverData.winners}
        currentPlayer={gameOverData.currentPlayer}
        finalScore={playerScore}
        onPlayAgain={handlePlayAgain}
        onBackToLobby={handleBackToLobby}
        onClose={() => {
          onHuntAgain();
          setShowLoserModal(false);
        }}
      />
    </div>
  );
};

export default Game;
